import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { Plus, Wallet, Copy, Ban, Pencil, Undo2, LayoutTemplate } from 'lucide-react';
import { ExpenseTemplateModal } from './OrgExpenseTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tl = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;
const trDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('tr-TR') : '-');
const KASA_LABELS = { nakit: 'Nakit', garanti_bbva: 'Garanti BBVA', enpara: 'Enpara', bostanci: 'Bostancı' };
const PIE = ['#059669', '#0891b2', '#6366f1', '#d97706', '#dc2626', '#7c3aed', '#0d9488', '#ca8a04'];
const ST = {
  pending: { label: 'Bekliyor', cls: 'bg-amber-100 text-amber-800' },
  partial: { label: 'Kısmi', cls: 'bg-blue-100 text-blue-800' },
  settled: { label: 'Tamamlandı', cls: 'bg-emerald-100 text-emerald-800' },
  iptal: { label: 'İptal', cls: 'bg-slate-200 text-slate-600' },
};

const blankTx = (type) => ({
  type, title: '', category: '', amount: '', date: new Date().toISOString().slice(0, 10),
  payment_method: 'nakit', session_id: '', status: 'pending', settled_amount: '',
  due_date: '', counterparty: '', document_no: '', description: '', notes: '',
  invoice_issued: false, vat_rate: 20,
});

const VAT_RATE = 20;
const vatCalc = (amount, invoiced, rate = VAT_RATE) => {
  const base = Number(amount) || 0;
  if (!invoiced) return { base, vat: 0, total: base };
  const vat = Math.round(base * rate) / 100;
  return { base, vat, total: Math.round((base + vat) * 100) / 100 };
};

export const OrgFinance = ({ orgId, type, sessions = [], onChanged }) => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState({ income: [], expense: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankTx(type));
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', date: '', payment_method: 'nakit', note: '' });
  const [showTemplate, setShowTemplate] = useState(false);
  const isIncome = type === 'income';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations/${orgId}/transactions`, { params: { type } });
      setItems(res.data?.items || []);
    } catch (e) { toast.error('Kayıtlar yüklenemedi'); }
    finally { setLoading(false); }
  }, [orgId, type]);

  useEffect(() => { load(); axios.get(`${API}/organization-categories`).then(r => setCats(r.data)).catch(() => {}); }, [load]);

  const submit = async () => {
    if (!form.title.trim() || !form.category) { toast.error('Başlık ve kategori zorunlu'); return; }
    if (!(Number(form.amount) > 0)) { toast.error('Tutar 0’dan büyük olmalı'); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        amount: Number(form.amount),
        invoice_issued: !!form.invoice_issued,
        vat_rate: form.invoice_issued ? (Number(form.vat_rate) || VAT_RATE) : null,
        settled_amount: Number(form.settled_amount) || 0,
        session_id: form.session_id || null,
        due_date: form.due_date || null,
      };
      if (editing) {
        await axios.put(`${API}/organization-transactions/${editing.id}`, body);
        toast.success('Kayıt güncellendi');
      } else {
        const res = await axios.post(`${API}/organizations/${orgId}/transactions`, body);
        toast.success(res.data?.message || 'Kaydedildi');
      }
      setShowForm(false); setEditing(null); setForm(blankTx(type));
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const openPay = (tx) => {
    const remaining = Math.max(0, (tx.amount || 0) - (tx.settled_amount || 0));
    setPayFor(tx);
    setPayForm({ amount: remaining, date: new Date().toISOString().slice(0, 10), payment_method: tx.payment_method || 'nakit', note: '' });
  };

  const submitPay = async () => {
    if (!(Number(payForm.amount) > 0)) { toast.error('Tutar girin'); return; }
    try {
      const res = await axios.post(`${API}/organization-transactions/${payFor.id}/payments`, {
        ...payForm, amount: Number(payForm.amount), client_token: `${payFor.id}-${Date.now()}`,
      });
      toast.success(res.data?.message || 'Kaydedildi');
      setPayFor(null); await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const reversePayment = async (tx, p) => {
    if (!window.confirm(`${tl(p.amount)} tutarlı ${isIncome ? 'tahsilat' : 'ödeme'} iptal edilecek ve kasaya iade kaydı yazılacak. Devam?`)) return;
    try {
      await axios.delete(`${API}/organization-transactions/${tx.id}/payments/${p.id}`);
      toast.success('İptal edildi, kasaya iade kaydı yazıldı');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'İptal edilemedi'); }
  };

  const cancelTx = async (tx) => {
    if (!window.confirm(`"${tx.title}" kaydı iptal edilecek. Gerçekleşmiş tahsilat/ödeme varsa kasaya iade kaydı yazılır. Devam?`)) return;
    try {
      const res = await axios.delete(`${API}/organization-transactions/${tx.id}`);
      toast.success(res.data?.message || 'İptal edildi');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'İptal edilemedi'); }
  };

  const duplicate = async (tx) => {
    try {
      await axios.post(`${API}/organization-transactions/${tx.id}/duplicate`);
      toast.success('Kopyalandı (bekleyen)');
      await load(); onChanged?.();
    } catch (e) { toast.error('Kopyalanamadı'); }
  };

  const startEdit = (tx) => {
    setEditing(tx);
    setForm({
      ...blankTx(type), ...tx,
      amount: tx.invoice_issued ? (tx.base_amount ?? tx.amount) : tx.amount,
      invoice_issued: !!tx.invoice_issued,
      vat_rate: tx.vat_rate || VAT_RATE,
      settled_amount: '', session_id: tx.session_id || '', due_date: tx.due_date || '',
    });
    setShowForm(true);
  };

  const total = items.filter(i => !i.is_cancelled).reduce((s, i) => s + (i.amount || 0), 0);
  const settled = items.filter(i => !i.is_cancelled).reduce((s, i) => s + (i.settled_amount || 0), 0);

  return (
    <div className="space-y-3" data-testid={`org-${type}-panel`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-3 text-xs">
          <span className="px-2 py-1 rounded bg-slate-100">Toplam: <strong>{tl(total)}</strong></span>
          <span className={`px-2 py-1 rounded ${isIncome ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
            {isIncome ? 'Tahsil edilen' : 'Ödenen'}: <strong>{tl(settled)}</strong>
          </span>
          <span className="px-2 py-1 rounded bg-amber-50 text-amber-800">
            {isIncome ? 'Bekleyen alacak' : 'Bekleyen ödeme'}: <strong>{tl(total - settled)}</strong>
          </span>
        </div>
        <div className="flex gap-1.5">
          {!isIncome && (
            <Button size="sm" variant="outline" className="h-8" onClick={() => setShowTemplate(true)} data-testid="org-template-open-btn">
              <LayoutTemplate className="w-3.5 h-3.5 mr-1" /> Gider Şablonu
            </Button>
          )}
          <Button size="sm" onClick={() => { setEditing(null); setForm(blankTx(type)); setShowForm(true); }}
            className={isIncome ? 'bg-emerald-600 hover:bg-emerald-700 h-8' : 'bg-rose-600 hover:bg-rose-700 h-8'}
            data-testid={`org-${type}-add-btn`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> {isIncome ? 'Gelir Ekle' : 'Gider Ekle'}
          </Button>
        </div>
      </div>

      {!isIncome && (
        <ExpenseTemplateModal open={showTemplate} onOpenChange={setShowTemplate} orgId={orgId}
          sessions={sessions} onApplied={async () => { await load(); onChanged?.(); }} />
      )}

      <div className="space-y-1.5 max-h-[420px] overflow-y-auto" data-testid={`org-${type}-list`}>
        {loading ? <p className="text-center py-8 text-slate-400 text-sm">Yükleniyor...</p>
          : items.length === 0 ? <p className="text-center py-8 text-slate-400 text-sm">Kayıt yok</p>
            : items.map(tx => (
              <div key={tx.id} className={`rounded-lg border p-2.5 ${tx.is_cancelled ? 'bg-slate-50 opacity-70' : 'bg-white'}`} data-testid={`org-tx-${tx.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {tx.title} {tx.is_cancelled && <span className="text-[10px] text-rose-600">(İPTAL)</span>}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {trDate(tx.date)} · {tx.category} · {KASA_LABELS[tx.payment_method] || tx.payment_method}
                      {tx.due_date ? ` · vade ${trDate(tx.due_date)}` : ''}
                      {tx.session_id ? ' · oturuma bağlı' : ''}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${isIncome ? 'text-emerald-700' : 'text-rose-700'}`}>{tl(tx.amount)}</p>
                    {tx.invoice_issued && (
                      <p className="text-[10px] text-amber-700" data-testid={`org-tx-vat-${tx.id}`}>
                        Faturalı · {tl(tx.base_amount ?? tx.amount)} + KDV {tl(tx.vat_amount || 0)}
                      </p>
                    )}
                    <Badge className={`text-[10px] ${ST[tx.status]?.cls || 'bg-slate-100'}`}>{ST[tx.status]?.label || tx.status}</Badge>
                  </div>
                </div>
                {(tx.settled_amount > 0 || (tx.payments || []).length > 0) && (
                  <div className="mt-1.5 pt-1.5 border-t space-y-0.5">
                    <p className="text-[11px] text-slate-600">
                      {isIncome ? 'Tahsil edilen' : 'Ödenen'}: <strong>{tl(tx.settled_amount)}</strong>
                      {tx.amount - tx.settled_amount > 0.01 && !tx.is_cancelled && ` · kalan ${tl(tx.amount - tx.settled_amount)}`}
                    </p>
                    {(tx.payments || []).map(p => (
                      <div key={p.id} className="flex items-center justify-between text-[11px]">
                        <span className={p.is_reversed ? 'line-through text-slate-400' : 'text-slate-600'}>
                          {trDate(p.date)} · {tl(p.amount)} · {KASA_LABELS[p.payment_method] || p.payment_method}
                          {p.is_reversed ? ' (iptal)' : ''}
                        </span>
                        {!p.is_reversed && !tx.is_cancelled && (
                          <Button size="sm" variant="ghost" className="h-5 px-1 text-rose-600" onClick={() => reversePayment(tx, p)} data-testid={`org-payment-reverse-${p.id}`}>
                            <Undo2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!tx.is_cancelled && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {tx.amount - (tx.settled_amount || 0) > 0.01 && (
                      <Button size="sm" className="h-6 text-[11px] bg-cyan-600 hover:bg-cyan-700" onClick={() => openPay(tx)} data-testid={`org-pay-${tx.id}`}>
                        <Wallet className="w-3 h-3 mr-1" /> {isIncome ? 'Tahsilat Al' : 'Ödeme Yap'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => startEdit(tx)} data-testid={`org-tx-edit-${tx.id}`}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => duplicate(tx)} data-testid={`org-tx-copy-${tx.id}`}><Copy className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="h-6 text-[11px] text-rose-700" onClick={() => cancelTx(tx)} data-testid={`org-tx-cancel-${tx.id}`}><Ban className="w-3 h-3" /></Button>
                  </div>
                )}
              </div>
            ))}
      </div>

      {/* Kayıt formu */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`org-${type}-form-modal`}>
          <DialogHeader>
            <DialogTitle>{editing ? 'Kaydı Düzenle' : (isIncome ? 'Yeni Gelir' : 'Yeni Gider')}</DialogTitle>
            <DialogDescription className="text-xs">
              Bekleyen kayıt kasayı etkilemez; {isIncome ? 'tahsil edilen' : 'ödenen'} tutar seçilen kasaya tek hareket olarak işlenir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            <div><Label className="text-xs">Başlık *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={`org-${type}-title`} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Kategori *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid={`org-${type}-category`}><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>{(isIncome ? cats.income : cats.expense).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{form.invoice_issued ? "Tutar (KDV'siz matrah) *" : 'Tutar (₺) *'}</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid={`org-${type}-amount`} />
              </div>
              <div><Label className="text-xs">İşlem Tarihi</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid={`org-${type}-date`} /></div>
              <div><Label className="text-xs">Vade Tarihi</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid={`org-${type}-due`} /></div>
              <div>
                <Label className="text-xs">Kasa / Banka</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid={`org-${type}-method`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(KASA_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Oturum (ops.)</Label>
                <Select value={form.session_id} onValueChange={(v) => setForm({ ...form, session_id: v === 'general' ? '' : v })}>
                  <SelectTrigger data-testid={`org-${type}-session`}><SelectValue placeholder="Organizasyon geneli" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Organizasyon geneli</SelectItem>
                    {sessions.map(s => <SelectItem key={s.id} value={s.id}>{trDate(s.date)} {s.start_time}–{s.end_time}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">{isIncome ? 'İlgili kişi/kurum' : 'Ödeme yapılan'}</Label><Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} data-testid={`org-${type}-counterparty`} /></div>
              <div><Label className="text-xs">Belge/Fatura No</Label><Input value={form.document_no} onChange={(e) => setForm({ ...form, document_no: e.target.value })} data-testid={`org-${type}-docno`} /></div>
            </div>
            {/* Faturalı / faturasız işlem */}
            <div className={`rounded-lg border p-2.5 space-y-2 ${form.invoice_issued ? 'border-amber-300 bg-amber-50/70' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, invoice_issued: false })}
                  className={`flex-1 h-8 rounded-md text-[12px] font-semibold border transition-colors ${!form.invoice_issued ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}
                  data-testid={`org-${type}-invoice-off`}>Faturasız İşlem</button>
                <button type="button" onClick={() => setForm({ ...form, invoice_issued: true })}
                  className={`flex-1 h-8 rounded-md text-[12px] font-semibold border transition-colors ${form.invoice_issued ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-600 border-slate-200'}`}
                  data-testid={`org-${type}-invoice-on`}>Faturalı İşlem (+KDV)</button>
              </div>
              {form.invoice_issued && (
                <div className="space-y-2" data-testid={`org-${type}-vat-block`}>
                  <div className="flex items-center gap-2">
                    <Label className="text-[11px] text-amber-900 whitespace-nowrap">KDV Oranı (%)</Label>
                    <Input type="number" step="1" className="h-8 w-24 bg-white" value={form.vat_rate}
                      onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                      data-testid={`org-${type}-vat-rate`} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[11px] text-amber-900" data-testid={`org-${type}-vat-preview`}>
                    <div>Matrah<div className="font-bold text-[13px]">{tl(vatCalc(form.amount, true, Number(form.vat_rate) || VAT_RATE).base)}</div></div>
                    <div>KDV %{Number(form.vat_rate) || VAT_RATE}<div className="font-bold text-[13px]">{tl(vatCalc(form.amount, true, Number(form.vat_rate) || VAT_RATE).vat)}</div></div>
                    <div>Toplam<div className="font-bold text-[13px] text-emerald-700">{tl(vatCalc(form.amount, true, Number(form.vat_rate) || VAT_RATE).total)}</div></div>
                  </div>
                </div>
              )}
            </div>
            {!editing && (
              <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 p-2.5 space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" className="h-4 w-4" checked={form.status === 'settled'}
                    onChange={(e) => setForm({ ...form, status: e.target.checked ? 'settled' : 'pending', settled_amount: e.target.checked ? vatCalc(form.amount, form.invoice_issued, Number(form.vat_rate) || VAT_RATE).total : '' })}
                    data-testid={`org-${type}-settled-check`} />
                  <Label className="text-xs cursor-pointer font-semibold text-cyan-900">
                    {isIncome ? 'Tahsil edildi (kasaya işle)' : 'Ödendi (kasadan düş)'}
                  </Label>
                </div>
                <div>
                  <Label className="text-[11px]">Kısmi tutar (boş = tamamı)</Label>
                  <Input type="number" step="0.01" value={form.settled_amount}
                    onChange={(e) => setForm({ ...form, settled_amount: e.target.value, status: Number(e.target.value) > 0 ? 'settled' : 'pending' })}
                    placeholder="Örn: 10000" data-testid={`org-${type}-settled-amount`} />
                </div>
              </div>
            )}
            <div><Label className="text-xs">Açıklama / Not</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={`org-${type}-description`} /></div>
            <Button onClick={submit} disabled={saving} className="w-full bg-slate-800 hover:bg-slate-900" data-testid={`org-${type}-submit`}>
              {saving ? 'Kaydediliyor...' : (editing ? 'Güncelle' : 'Kaydet')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tahsilat / ödeme modalı */}
      <Dialog open={!!payFor} onOpenChange={(v) => { if (!v) setPayFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="org-payment-modal">
          <DialogHeader>
            <DialogTitle>{isIncome ? 'Tahsilat Al' : 'Ödeme Yap'}</DialogTitle>
            <DialogDescription className="text-xs">
              {payFor?.title} · kalan {tl((payFor?.amount || 0) - (payFor?.settled_amount || 0))}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            <div><Label className="text-xs">Tutar (₺)</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="org-payment-amount" /></div>
            <div><Label className="text-xs">Tarih</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} data-testid="org-payment-date" /></div>
            <div>
              <Label className="text-xs">Kasa / Banka</Label>
              <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                <SelectTrigger data-testid="org-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(KASA_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Not</Label><Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} data-testid="org-payment-note" /></div>
            <Button onClick={submitPay} className="w-full bg-cyan-700 hover:bg-cyan-800" data-testid="org-payment-submit">Kaydet ve Kasaya İşle</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const OrgFinancialReport = ({ orgId }) => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}/financials`);
      setData(res.data);
    } catch (e) { /* sessiz */ }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-center py-8 text-slate-400 text-sm">Yükleniyor...</p>;
  const s = data.summary;
  const budget = [
    { name: 'Gelir', Planlanan: s.planned_income, Gerçekleşen: s.income_total, Tahsil: s.income_collected },
    { name: 'Gider', Planlanan: s.planned_expense, Gerçekleşen: s.expense_total, Tahsil: s.expense_paid },
  ];

  return (
    <div className="space-y-3" data-testid="org-financial-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['Net Kâr/Zarar', s.net_profit, s.net_profit < 0 ? 'text-rose-700' : 'text-emerald-700'],
          ['Nakit Bazlı Sonuç', s.cash_result, 'text-cyan-700'],
          ['Bekleyen Alacak', s.pending_receivable, 'text-amber-700'],
          ['Bekleyen Ödeme', s.pending_payable, 'text-orange-700'],
          ['Gerçekleşen Gelir', s.income_total, 'text-emerald-700'],
          ['Gerçekleşen Gider', s.expense_total, 'text-rose-700'],
          ['Geciken Alacak', s.overdue_receivable, 'text-rose-700'],
          ['Geciken Ödeme', s.overdue_payable, 'text-rose-700']].map(([l, v, cls]) => (
          <div key={l} className="rounded-lg border bg-white p-2.5">
            <p className="text-[10px] text-slate-500 uppercase">{l}</p>
            <p className={`text-sm font-bold ${cls}`}>{tl(v)}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-600">Kâr Marjı: <strong>{s.profit_margin}%</strong></p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg border bg-white p-3">
          <p className="text-xs font-semibold mb-2">Planlanan / Gerçekleşen</p>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={budget}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => tl(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Planlanan" fill="#94a3b8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Gerçekleşen" fill="#6366f1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Tahsil" fill="#059669" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border bg-white p-3">
          <p className="text-xs font-semibold mb-2">Aylık Gelir-Gider</p>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => tl(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="income" name="Gelir" stroke="#059669" strokeWidth={2} />
              <Line type="monotone" dataKey="expense" name="Gider" stroke="#dc2626" strokeWidth={2} />
              <Line type="monotone" dataKey="net" name="Net" stroke="#6366f1" strokeWidth={2} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {[['Gelir Dağılımı', data.income_by_category], ['Gider Dağılımı', data.expense_by_category]].map(([title, rows]) => (
          <div key={title} className="rounded-lg border bg-white p-3">
            <p className="text-xs font-semibold mb-2">{title}</p>
            {rows.length === 0 ? <p className="text-xs text-slate-400 py-8 text-center">Kayıt yok</p> : (
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} label={(e) => e.name?.slice(0, 12)} labelLine={false}>
                    {rows.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => tl(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-white overflow-x-auto">
        <p className="text-xs font-semibold p-2 border-b">Oturum Bazlı Finans</p>
        <table className="w-full text-xs" data-testid="org-session-finance-table">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left px-2 py-1.5">Oturum</th>
              <th className="text-left px-2 py-1.5">Salon</th>
              <th className="text-right px-2 py-1.5">Gelir</th>
              <th className="text-right px-2 py-1.5">Gider</th>
              <th className="text-right px-2 py-1.5">Net</th>
            </tr>
          </thead>
          <tbody>
            {data.sessions.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-5 text-slate-400">Oturuma bağlı finansal kayıt yok</td></tr>
            ) : data.sessions.map((r, i) => (
              <tr key={r.session_id} className={i % 2 ? 'bg-slate-50' : ''}>
                <td className="px-2 py-1.5">{r.label}</td>
                <td className="px-2 py-1.5">{r.salon_name}</td>
                <td className="px-2 py-1.5 text-right text-emerald-700">{tl(r.income)}</td>
                <td className="px-2 py-1.5 text-right text-rose-700">{tl(r.expense)}</td>
                <td className={`px-2 py-1.5 text-right font-bold ${r.net < 0 ? 'text-rose-700' : ''}`}>{tl(r.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
